// crewbit-admin/src/components/money/RefundModal.tsx
//
// Unified refund confirm modal — branches by invoice.stripe_payment_intent_id
// presence per Decision #586:
//   • Stripe-paid → POST refund-invoice-via-stripe edge fn (Phase 5 substrate)
//   • Manual-paid → mark_invoice_refunded_manually RPC (existing)
// Both paths share amount + reason inputs (Decision #585 — partial-refund
// support). Title + body copy + loading copy + button label adapt per path.

import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { palette, spacing, typography, transition } from '../../styles/tokens';
import { MoneyInput } from '../common/MoneyInput';
import {
  canonicalMoney, parseMoney, formatMoney, subtractMoney,
  isPositive, compareMoney,
} from '../../lib/money';
import { markRefundedManually, refundViaStripe } from '../../lib/invoiceMutations';
import { lookupErrorCopy, type MoneyErrorCode } from '../../lib/errorMap';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  total: number | string;
  alreadyRefunded: number | string;
  /** Present → Stripe refund path; null/absent → manual-record path. */
  stripePaymentIntentId?: string | null;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

export function RefundModal({ isOpen, invoiceId, total, alreadyRefunded, stripePaymentIntentId, onClose, onResult }: Props) {
  const remaining = subtractMoney(canonicalMoney(total), canonicalMoney(alreadyRefunded));
  const [amount, setAmount] = useState(remaining);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const isStripePath = !!stripePaymentIntentId;
  const title = isStripePath ? 'Refund via Stripe' : 'Record manual refund';
  const loadingLabel = isStripePath ? 'Processing Stripe refund…' : 'Recording…';
  const confirmLabel = isStripePath ? 'Refund via Stripe' : 'Record refund';

  const parsed = parseMoney(amount);
  const valid = isPositive(parsed) && compareMoney(parsed, remaining) <= 0 && reason.trim().length > 0;

  const onConfirm = async () => {
    if (!valid) return;
    setLoading(true);
    if (isStripePath) {
      const result = await refundViaStripe({
        invoiceId, amount: Number(parsed), reason: reason.trim(),
      });
      setLoading(false);
      if (result.success) {
        onResult(true, `Stripe refund processed (${result.data.new_status.replace('_', ' ')}).`);
        onClose();
      } else {
        onResult(false, lookupErrorCopy('mark_invoice_refunded_manually', result.error.code as MoneyErrorCode));
      }
    } else {
      const result = await markRefundedManually(invoiceId, {
        amount: Number(parsed), reason: reason.trim(),
      });
      setLoading(false);
      if (result.success) {
        onResult(true, result.data.already_processed ? 'Refund already processed.' : 'Refund recorded.');
        onClose();
      } else {
        onResult(false, result.error.message ?? 'Failed to record refund.');
      }
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 440, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.amberWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <RotateCcw size={22} color={palette.amber} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          {title}
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          {isStripePath ? 'Processed through Stripe. ' : 'Records the refund locally. '}
          Up to <span style={{ color: palette.g800, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(remaining)}</span> remaining on this invoice.
        </div>

        <div style={{ display: 'grid', gap: spacing.sm }}>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
              Amount
            </div>
            <MoneyInput value={amount} onChange={setAmount} ariaLabel="Refund amount" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
              Reason
            </div>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 200))}
              placeholder="e.g. Client returned the materials"
              style={{
                width: '100%', padding: spacing.sm, borderRadius: 8,
                border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
                fontSize: typography.size.caption, color: palette.g800,
                boxSizing: 'border-box',
              }}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || !valid} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: (loading || !valid) ? palette.g300 : palette.amber,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: (loading || !valid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? loadingLabel : confirmLabel}</button>
        </div>
      </div>
    </>
  );
}
